import React, { FC, useEffect, useMemo, useRef, useState } from 'react'

import { format as formatDate } from 'date-fns'
import { useDrag } from 'react-dnd'

import { openFiles } from './utilities'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { Checkbox } from '@/components/Checkbox'
import Spinner from '@/components/Spinner'
import { FileTypeIcon } from '@/features/FileManager/FileTypeIcon'
import { FilesDotsMenu } from '@/features/FileManager/FilesDotsMenu'
import { FileManagerFile, clearFiles, toggleFile } from '@/features/FileManager/fileManagerSlice'
import { useCurrentApplication } from '@/graphql/services/application'
import { formatBytes } from '@/utils'
import { useMousePosition } from '@/utils/useMousePosition'

const DragPreview = (props: { text: string }) => {
  const { x, y } = useMousePosition('dragover')

  if (!x || !y) return null

  return (
    <div
      style={{ position: 'fixed', top: y, left: x + 20 }}
      className='bg-sky-400 p-4 z-50'
    >
      {props.text}
    </div>
  )
}

export const FileRow: FC<{ file: FileManagerFile }> = (props) => {
  const { file } = props

  const dispatch = useAppDispatch()
  const { selectedFiles } = useAppSelector((state) => state.fileManager)

  const { data: application } = useCurrentApplication()

  const isSelected = useMemo(
    () => !!selectedFiles.find((it) => it.uuid === file.uuid),
    [file, selectedFiles]
  )

  const rightClickMenuRef = useRef(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Handle click outside for right-click menu
  function TriggerOutsideAlert(ref: React.MutableRefObject<any>) {
    useEffect(() => {
      function handleClickOutside(event: Event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowRightClickMenu(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])
  }

  TriggerOutsideAlert(rightClickMenuRef)

  const [rightClickMenuPosition, setRightClickMenuPosition] = useState({
    x: null,
    y: null,
  })
  const [showRightClickMenu, setShowRightClickMenu] = useState(false)

  const canDrag = useMemo(
    () => isSelected || selectedFiles.length === 0,
    [isSelected, selectedFiles]
  )

  const [{ isDragging }, dragRef, dragPreviewRef] = useDrag(() => {
    return {
      type: 'file',
      item: file,
      canDrag,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }
  }, [canDrag])

  return (
    <div
      ref={dragRef}
      className={`flex justify-start items-center cursor-pointer select-none relative ${
        isSelected && 'bg-gray-400'
      } hover:bg-sky-300`}
      onContextMenu={(e) => {
        e.preventDefault()

        // If right click on not selected file, deselect other files
        if (!selectedFiles.includes(file)) {
          dispatch(clearFiles())
        }

        setRightClickMenuPosition({ x: e.clientX, y: e.clientY })
        setShowRightClickMenu(true)
      }}
    >
      {/* Intentionally empty to hide default preview */}
      <div ref={dragPreviewRef} />

      {showRightClickMenu && (
        <div
          ref={rightClickMenuRef}
          className='fixed z-10'
          style={{
            left: rightClickMenuPosition.x,
            top: rightClickMenuPosition.y,
          }}
        >
          <FilesDotsMenu file={file} />
        </div>
      )}

      {isDragging && (
        <DragPreview
          text={
            selectedFiles.length ? `Move ${selectedFiles.length} file(s)` : `Move ${file.fileName}`
          }
        />
      )}

      <div className='flex-1 flex gap-2 items-center justify-between overflow-visible px-2 min-h-[36px] relative'>
        <Checkbox
          color={'rgb(30 64 175)'}
          value={isSelected ? 'checked' : 'unchecked'}
          onClick={() => dispatch(toggleFile(file))}
        />
        <FileTypeIcon type={file.fileName?.split('.')?.slice(-1)[0]} />
        <p
          className='w-full'
          onClick={() => openFiles([file], application.externalId)}
        >
          {file.fileName || 'unnamed_file'}
        </p>

        {isLoading && (
          <div className='flex justify-center items-center'>
            <Spinner size={5} />
          </div>
        )}
      </div>
      <div className='w-[170px] p-2 text-ellipsis overflow-hidden whitespace-nowrap'>
        {file.size ? formatBytes(file.size) : '-'}
      </div>
      <div className='w-[170px] p-2 text-ellipsis overflow-hidden whitespace-nowrap'>
        {file.modifiedAt ? formatDate(new Date(file.modifiedAt), 'MM/dd/yyyy h:mm a') : '-'}
      </div>
    </div>
  )
}
